import * as React from 'react';
import {
  Box,
  Button,
  InputProps,
  UnorderedList,
  ListItem,
  useFormControl,
  useMultiStyleConfig,
  useToast,
  Icon,
  IconButton,
  Flex,
} from '@chakra-ui/react';
import {
  useDropzone,
  FileWithPath,
  Accept,
  FileRejection,
} from 'react-dropzone';
import adserver from 'services/adserver';
import { formatApiErrors } from 'helpers';
import { FiTrash } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { selectDataUploadLimit } from 'store/consts';
import { useAppSelector } from 'hooks';

type FileDataType = {
  name?: string;
  uploadedFile?: string;
};

export type InputFileWithUploadType = {
  basketId: number;
  fileId?: number;
  accept?: Accept;
  minSize?: number;
  maxSize?: number;
  isDisabled?: boolean;
  isInvalid?: boolean;
  onChange: (fileId?: number) => void;
};

/**
 * Komponent obsługujący upload plików na potrzeby kreacji
 * Plik jest wysyłany do endpointa API, zwracane jest id, które to staje się wartością pola formularza
 */
export const InputFileBasket = ({
  basketId,
  fileId,
  isDisabled,
  accept,
  onChange,
  size,
  variant,
  minSize,
  maxSize,
  isInvalid,
  ...props
}: InputFileWithUploadType & InputProps): JSX.Element => {
  const { t } = useTranslation();
  const dataUploadLimit = useAppSelector(selectDataUploadLimit);
  const toast = useToast({
    position: 'bottom',
    status: 'error',
    isClosable: true,
  });
  const chakraInputProps = useFormControl(props);
  const chakraInputStyle = useMultiStyleConfig('InputFile', {
    size,
    variant,
  });
  const [fileData, setFileData] = React.useState<FileDataType | undefined>();
  /**
   * status uploadowania pliku: undefined dla idle lub cyfra 0-100 oznaczająca procent uploadu
   */
  const [uploadingProgress, setUploadingProgress] = React.useState<
    number | undefined
  >();

  //const isBuyer = useAppSelector(isUserBuyer);
  /* TODO DELETE  
  const hasPublisherStatuses = useBasketStatusID(
    [
      PUBLISHER_GENERATE_ORDER_REQUESTED,
      PUBLISHER_CORRECTION_REQUESTED,
      PUBLISHER_ACCEPTANCE_REQUESTED,
    ],
    basketId,
  );
  const hasBuyerStatuses = useBasketStatusID(
    [BUYER_SIGNATURE_REQUESTED, BUYER_CORRECTION_REQUESTED],
    basketId,
  ); */
  const handleAcceptedFiles = (acceptedFiles: FileWithPath[]) => {
    if (acceptedFiles.length) {
      uploadFile(acceptedFiles[0]);
    }
  };

  const handleRejectedFiles = (fileRejections: FileRejection[]) => {
    fileRejections.forEach(fileRejection => {
      toast({
        title: 'Plik odrzucony',
        description: (
          <UnorderedList>
            {fileRejection.errors.map(error => (
              <ListItem key={error.code}>{error.message}</ListItem>
            ))}
          </UnorderedList>
        ),
      });
    });
  };

  const handleClearFile = () => {
    setFileData(undefined);
    setUploadingProgress(undefined);
    if (onChange) onChange(undefined);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDropAccepted: handleAcceptedFiles,
    onDropRejected: handleRejectedFiles,
    maxFiles: 1,
    disabled: isDisabled,
    accept,
    minSize,
    maxSize: maxSize ?? (dataUploadLimit || undefined),
  });

  async function getUploadedFileData(fileId: number) {
    const result = await adserver({
      url: `/basket/${basketId}/order-value/${fileId}`,
      headers: {},
    });
    setFileData(result.data);
  }

  async function uploadFile(file: FileWithPath) {
    try {
      setFileData({
        name: file.name,
        uploadedFile: URL.createObjectURL(file),
      });
      setUploadingProgress(undefined);
      const url = `/basket/${basketId}/order-value/`;
      const method = 'POST';
      const data = new FormData();
      data.append('uploadedFile', file);
      data.append('name', file.name);

      const result = await adserver({
        url,
        method,
        data,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (status: { loaded: number; total: number }) =>
          setUploadingProgress(
            Math.round((status.loaded * 100) / status.total),
          ),
      });

      setFileData(result.data);

      if (!onChange) return;
      onChange(result.data?.id || undefined);
    } catch (error) {
      toast({
        title: 'Błąd podczas wysyłania pliku',
        description: formatApiErrors(error),
      });

      setFileData(undefined);

      if (onChange) onChange(undefined);
    }
  }

  React.useEffect(() => {
    if (fileId) {
      getUploadedFileData(fileId);
    } else {
      setFileData(undefined);
    }
  }, [fileId]);

  return (
    <Box {...props}>
      <Flex
        width='100%'
        alignContent='stretch'
        alignItems='center'
        marginBottom={2}
      >
        <Box
          flex='auto'
          __css={chakraInputStyle.field}
          {...chakraInputProps}
          {...getRootProps()}
          minHeight='56px'
          display='flex'
          alignItems='center'
          borderWidth={1}
          borderColor={
            isInvalid
              ? 'red.500'
              : uploadingProgress === 100
              ? 'green.400'
              : 'gray.200'
          }
        >
          <input {...getInputProps()} />
          <Flex align='center' flex='1' minH='40px'>
            {typeof uploadingProgress === 'number' &&
              (uploadingProgress < 100 ? (
                <Box mr={2} display='flex' alignItems='center'>
                  <svg width='20' height='20' viewBox='0 0 20 20'>
                    <circle
                      cx='10'
                      cy='10'
                      r='8'
                      fill='none'
                      stroke='#3182ce'
                      strokeWidth='3'
                      strokeDasharray={Math.PI * 2 * 8}
                      strokeDashoffset={
                        Math.PI * 2 * 8 * (1 - uploadingProgress / 100)
                      }
                      style={{ transition: 'stroke-dashoffset 0.2s' }}
                    />
                  </svg>
                </Box>
              ) : (
                <Box
                  mr={2}
                  color='green.400'
                  display='flex'
                  alignItems='center'
                >
                  <svg width='20' height='20' viewBox='0 0 20 20'>
                    <circle
                      cx='10'
                      cy='10'
                      r='8'
                      fill='none'
                      stroke='#38A169'
                      strokeWidth='3'
                    />
                    <polyline
                      points='6,11 9,14 14,8'
                      fill='none'
                      stroke='#38A169'
                      strokeWidth='2.5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </Box>
              ))}

            {isInvalid && (
              <Box color='red.500' mr={2}>
                <svg
                  width='24'
                  height='24'
                  viewBox='0 0 24 24'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M12 1.00293H12.0049C13.4396 0.972313 14.8661 1.22822 16.2002 1.75684C17.5344 2.28552 18.75 3.07596 19.7744 4.08105C20.7986 5.08599 21.6111 6.28576 22.165 7.60938C22.719 8.93326 23.0034 10.3549 23 11.79V11.8008C23.0239 14.7428 21.8795 17.5743 19.8184 19.6738C17.757 21.7735 14.9462 22.9697 12.0039 23H11.7988C10.3807 23.0025 8.97575 22.7249 7.66504 22.1836C6.35448 21.6423 5.1631 20.848 4.16016 19.8457C3.15731 18.8434 2.36237 17.6529 1.82031 16.3428C1.27828 15.0326 1.00033 13.6278 1.00195 12.21V12.2012L1.00098 12.1924C0.977385 10.7409 1.24686 9.29912 1.79297 7.9541C2.33904 6.6092 3.15104 5.38841 4.17969 4.36426L4.1875 4.35547C6.24991 2.25493 9.05656 1.05112 12 1.00293ZM12.001 4.50195C11.4707 4.50195 10.962 4.71297 10.5869 5.08789C10.2119 5.46294 10.001 5.97154 10.001 6.50195V12.501C10.001 13.0314 10.2119 13.54 10.5869 13.915C10.7062 14.0343 10.8398 14.1352 10.9824 14.2197C10.9807 14.2205 10.9793 14.2219 10.9775 14.2227C10.6764 14.3554 10.4041 14.5472 10.1777 14.7861C9.95158 15.025 9.77534 15.3065 9.65918 15.6143C9.54307 15.922 9.48895 16.2504 9.50098 16.5791V16.5811C9.52615 17.233 9.80304 17.8506 10.2725 18.3037C10.7419 18.7566 11.3682 19.0106 12.0205 19.0127H12.0674C12.3965 19.0071 12.7212 18.9353 13.0225 18.8027C13.3237 18.6701 13.5959 18.4791 13.8223 18.2402C14.0486 18.0012 14.2257 17.7192 14.3418 17.4111C14.4579 17.103 14.5113 16.7744 14.499 16.4453C14.4746 15.7928 14.1983 15.1751 13.7285 14.7217C13.5138 14.5144 13.265 14.3513 12.9971 14.2324C13.148 14.1455 13.2897 14.0403 13.415 13.915C13.7899 13.54 14.001 13.0312 14.001 12.501V6.50195C14.001 5.97163 13.79 5.46292 13.415 5.08789C13.0401 4.71294 12.5312 4.50206 12.001 4.50195Z'
                    stroke='#E53E3E'
                    strokeWidth='2'
                  />
                </svg>
              </Box>
            )}
            <Box display='flex' alignItems='center'>
              {fileData?.name}
            </Box>
          </Flex>
          <Button
            colorScheme='gray'
            size='sm'
            sx={chakraInputStyle.action}
            isDisabled={isDisabled}
          >
            {t('selectFile', 'Wybierz plik')}
          </Button>
        </Box>
        <IconButton
          m={2}
          aria-label={t('deleteFile', 'Usuń plik')}
          onClick={handleClearFile}
          icon={<Icon as={FiTrash} />}
          isDisabled={isDisabled}
        />
      </Flex>
    </Box>
  );
};
